import { DRAWING_COLORS, LINE_STYLES } from './constants';
import type { DrawingAnnotation } from '@shared/types';
import type { ViewBox } from '@/utils/svgPathParser';

const C = DRAWING_COLORS;
const JP_FONT = "'Noto Sans JP', sans-serif";

type AnnotationLayerProps = {
  annotations: DrawingAnnotation[];
  viewType: string;
  viewBox: ViewBox;
  vpWidth: number;
  vpHeight: number;
};

/**
 * Project a 3D annotation position into 2D view coordinates.
 * Uses the same rotation logic as wrapWithProjection.
 */
export function projectTo2D(
  pos: { x: number; y: number; z: number },
  viewType: string,
): { x: number; y: number } {
  switch (viewType) {
    case 'top':
      return { x: pos.x, y: -pos.y };
    case 'front':
      return { x: pos.x, y: -pos.z };
    case 'right':
      return { x: -pos.y, y: -pos.z };
    case 'rear':
      return { x: -pos.x, y: -pos.z };
    case 'left':
      return { x: pos.y, y: -pos.z };
    case 'bottom':
      return { x: pos.x, y: pos.y };
    default:
      return { x: pos.x, y: -pos.z };
  }
}

/**
 * Map a projected 2D coordinate into SVG viewport space using the actual viewBox.
 */
function toViewportCoords(
  projected: { x: number; y: number },
  viewBox: ViewBox,
  vpWidth: number,
  vpHeight: number,
): { x: number; y: number } {
  const scaleX = vpWidth / viewBox.width;
  const scaleY = vpHeight / viewBox.height;
  const scale = Math.min(scaleX, scaleY);
  const offsetX = (vpWidth - viewBox.width * scale) / 2;
  const offsetY = (vpHeight - viewBox.height * scale) / 2;

  return {
    x: offsetX + (projected.x - viewBox.minX) * scale,
    y: offsetY + (projected.y - viewBox.minY) * scale,
  };
}

export function AnnotationLayer({
  annotations, viewType, viewBox, vpWidth, vpHeight,
}: AnnotationLayerProps) {
  const viewAnnotations = annotations.filter((a) => a.view === viewType);
  if (viewAnnotations.length === 0) return null;

  return (
    <g className="annotation-layer">
      {viewAnnotations.map((ann, i) => {
        const pos2d = projectTo2D(ann.position, viewType);
        const raw = toViewportCoords(pos2d, viewBox, vpWidth, vpHeight);

        // If the annotation's anchor falls outside the viewport, clamp it to
        // the edge so the leader-line + label still render in-view. This
        // surfaces feature callouts that would otherwise be silently dropped
        // when the projection's bbox is tighter than the part's position.
        const margin = 6;
        const clamped = {
          x: Math.min(Math.max(raw.x, margin), vpWidth - margin),
          y: Math.min(Math.max(raw.y, margin), vpHeight - margin),
        };

        return (
          <AnnotationMark key={i} annotation={ann} x={clamped.x} y={clamped.y} />
        );
      })}
    </g>
  );
}

function AnnotationMark({
  annotation, x, y,
}: {
  annotation: DrawingAnnotation;
  x: number;
  y: number;
}) {
  const leaderLen = 12;
  const textX = x + leaderLen;
  const textY = y - 4;
  const fontSize = 2.5;

  let displayText = annotation.text;
  if (annotation.type === 'process') {
    displayText = `◇(${annotation.text})`;
  } else if (annotation.type === 'reference') {
    displayText = `[${annotation.text}]`;
  }

  return (
    <g className={`annotation-${annotation.type}`}>
      {/* Leader line */}
      <line
        x1={x} y1={y}
        x2={textX} y2={textY}
        stroke={C.line}
        strokeWidth={LINE_STYLES.dimension.strokeWidth}
      />
      {/* Shelf line */}
      <line
        x1={textX} y1={textY}
        x2={textX + displayText.length * fontSize * 0.7} y2={textY}
        stroke={C.line}
        strokeWidth={LINE_STYLES.dimension.strokeWidth}
      />
      {/* Annotation dot */}
      <circle cx={x} cy={y} r={0.5} fill={C.line} />
      {/* Text */}
      <text
        x={textX + 1}
        y={textY - 1.5}
        fontSize={fontSize}
        fontFamily={JP_FONT}
        fill={C.text}
        dominantBaseline="auto"
      >
        {displayText}
      </text>
    </g>
  );
}
